// Data + mutation hooks for the facility-community page (S2.2). A thin layer over
// the business-local `@/libs/communityApi` client; it owns NO business logic —
// authorization is server-authoritative (the FE permission hook only shows/enables
// a control). Tests mock `@/libs/communityApi` and exercise the REAL hooks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getFeedIdentityGeneration,
  invalidateFeedQueries,
  mergeConfirmedFeedPost,
  removeConfirmedFeedPost,
  replaceConfirmedFeedPost,
  scheduleFeedReconciliation,
  useFeed,
} from "@bw/shared/hooks";

import {
  archiveCommunity,
  createCommunityPost,
  createFacilityCommunity,
  deleteCommunityPost,
  getFacilityCommunity,
  updateCommunity,
  updateCommunityPost,
} from "@/libs/communityApi";
import type {
  CommunityCreateInput,
  CommunityResponse,
  CommunityUpdate,
  PostResponse,
} from "@/libs/communityApi";
import { checkFacilityPermission } from "@/libs/permissionsApi";

// The query key is shared by the reader + the mutations' cache invalidation so a
// create / update refreshes the page's tri-state read.
function facilityCommunityKey(facilityId: string | null) {
  return ["facility-community", facilityId] as const;
}

/**
 * The TRI-STATE reader behind the page's two states.
 *   - facilityId falsy        -> the query is DISABLED (no fetch).
 *   - data === null           -> the real "no community yet" EMPTY state. The
 *     backend GET returns 200 + null (NOT a 404), so this is a RESOLVED query
 *     whose data is null — never surfaced as an error.
 *   - data === CommunityResponse -> ready (the manage view).
 *   - the read rejects        -> isError (the error banner).
 */
export function useFacilityCommunity(facilityId: string | null) {
  return useQuery<CommunityResponse | null>({
    queryKey: facilityCommunityKey(facilityId),
    queryFn: () => getFacilityCommunity(facilityId as string),
    enabled: !!facilityId,
  });
}

/**
 * Create the facility's community. Bound to the resolved facility id. On success/
 * failure it invalidates the reader so the page's tri-state read refreshes; the
 * USER-FACING toast (success / honest error — never a phantom success) is owned by
 * the calling component, which awaits `mutateAsync` directly. The page ALSO
 * refetches its reader after the mutation settles so a 409 (the community already
 * exists) flips it to the manage view even when the query cache is otherwise warm.
 */
export function useCreateFacilityCommunity(facilityId: string) {
  const queryClient = useQueryClient();
  return useMutation<CommunityResponse, unknown, CommunityCreateInput>({
    mutationFn: (data: CommunityCreateInput) =>
      createFacilityCommunity(facilityId, data),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: facilityCommunityKey(facilityId),
      });
    },
  });
}

/**
 * Edit the facility's community. Bound to the community id (PATCH /communities/{id}).
 * On success it invalidates the reader; the USER-FACING toast (success / honest
 * error — a backend 403 despite an allowed FE state is the authoritative answer,
 * never a phantom success) is owned by the calling component.
 */
export function useUpdateCommunity(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation<CommunityResponse, unknown, CommunityUpdate>({
    mutationFn: (data: CommunityUpdate) => updateCommunity(communityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility-community"] });
    },
  });
}

/**
 * S2.4 — READ the community's canonical cursor Feed. Mirrors
 * useFacilityCommunity's gating: a falsy community id DISABLES the fetch (no
 * call). Resolves canonical cursor pages; a rejected read lands on isError,
 * which drives the feed's error affordance. Owns no business logic.
 */
export function useFacilityCommunityPosts(communityId: string | null) {
  return useFeed(
    { scope: "community", communityId: communityId ?? "", limit: 20 },
    {
      enabled: Boolean(communityId),
      freshness: { enabled: Boolean(communityId), intervalMs: 30_000 },
    },
  );
}

/**
 * S2.4 — POST an announcement into the community (bound to the community id).
 * On success it merges the exact confirmed response into the loaded Feed and
 * schedules bounded projection reconciliation. The USER-FACING toast (success / honest error —
 * never a phantom success; a backend 403 despite an allowed FE gate is the
 * authoritative answer) is owned by the calling component, which awaits
 * `mutateAsync` directly. RBAC (facility:community) is server-enforced.
 */
export function useCreateCommunityPost(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    PostResponse,
    unknown,
    { content: string; media_urls?: string[] },
    number
  >({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    mutationFn: (data: { content: string; media_urls?: string[] }) =>
      createCommunityPost(communityId, data),
    onSuccess: (post, _variables, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      mergeConfirmedFeedPost(queryClient, post, [
        { scope: "community", subject: communityId },
      ]);
      scheduleFeedReconciliation(queryClient, [
        { scope: "community", subject: communityId },
        { scope: "home" },
        { scope: "communities" },
        { scope: "user", subject: post.author_id },
      ]);
    },
  });
}

/**
 * Author-edit: PATCH a post's CONTENT (the business surface never touches
 * media_urls — exclude_unset leaves the existing photos untouched; see
 * `updateCommunityPost`). The mutation variables carry the POST id + the new
 * content; the hook is bound to the community id for targeted reconciliation.
 * The edited content and server-set `edited_at` replace cards only after success.
 * toast is owned by the caller; a 403/404/422 propagates (no phantom success).
 */
export function useUpdateCommunityPost(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    PostResponse,
    unknown,
    { postId: string; content: string },
    number
  >({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    mutationFn: ({ postId, content }: { postId: string; content: string }) =>
      updateCommunityPost(postId, { content }),
    onSuccess: (post, _variables, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      replaceConfirmedFeedPost(queryClient, post);
      scheduleFeedReconciliation(queryClient, [
        { scope: "community", subject: communityId },
      ]);
    },
  });
}

/**
 * S2.4 — MODERATE: remove a post (DELETE /communities/posts/{postId}). The
 * mutation variable is the POST id (not the community id); the hook is bound to
 * the community id for targeted reconciliation. The confirmed deletion removes
 * loaded cards before bounded server rechecks. The toast is owned by the caller;
 * a 403 propagates and never causes a phantom removal.
 */
export function useDeleteCommunityPost(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string, number>({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    mutationFn: (postId: string) => deleteCommunityPost(postId),
    onSuccess: (_result, postId, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      removeConfirmedFeedPost(queryClient, postId);
      scheduleFeedReconciliation(queryClient, [
        { scope: "community", subject: communityId },
        { scope: "home" },
        { scope: "communities" },
      ]);
    },
  });
}

/**
 * Archive (soft-delete) the facility's community — DELETE /communities/{id}, which
 * sets the community's status to "archived" (data retained; hidden from players).
 * Bound to the community id. On SUCCESS it invalidates BOTH the facility-community
 * reader (so the page's tri-state read re-resolves to the archived state) and this
 * community's feed. The USER-FACING toast (success / honest error — a backend 403
 * for a non-owner is the authoritative answer, never a phantom success) is owned by
 * the calling component, which awaits `mutateAsync` directly. RBAC (entity-owner OR
 * facility owner) is server-enforced.
 */
export function useArchiveCommunity(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => archiveCommunity(communityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility-community"] });
      invalidateFeedQueries(queryClient, [
        { scope: "community", subject: communityId },
        { scope: "home" },
        { scope: "communities" },
      ]);
    },
  });
}

/**
 * Server-authoritative permission check for a facility action. The FE NEVER
 * derives a permission locally — it asks the backend (`permission.can()`). This
 * only shows/enables a control; the backend re-enforces on the mutation. While the
 * check is in flight the control is treated as NOT-allowed (fail-closed), so a
 * denied operator never sees a usable control flash.
 */
export function useFacilityPermission(
  facilityId: string,
  action: string,
): { allowed: boolean } {
  const query = useQuery<{ allowed: boolean }>({
    queryKey: ["facility-permission", facilityId, action],
    queryFn: () => checkFacilityPermission(facilityId, action),
    // An empty id (no facility resolved yet) disables the fetch — the control is
    // treated as NOT-allowed (fail-closed) until a real grant comes back.
    enabled: !!facilityId,
  });
  return { allowed: query.data?.allowed ?? false };
}
